/**
 * /dashboard/knowledge — Industry Knowledge Editor.
 *
 * Lets the logged-in agent customise the knowledge pack their AI uses on
 * voice + SMS. Customer overrides are merged on top of the default
 * industry pack at prompt-composition time (server side), so changes
 * apply instantly to the next conversation.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  BookOpen, Plus, Trash2, Save, Loader2, AlertCircle, Sparkles, X,
  Wand2, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_LIMITS = {
  max_faqs: 10, max_escalation_rules: 10, max_services: 30,
  max_pricing_notes: 500, max_custom_notes: 2000,
};

export default function DashboardKnowledgePage() {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [pack, setPack]         = useState(null);

  const [faqs, setFaqs]                 = useState([]);
  const [services, setServices]         = useState([]);
  const [pricingNotes, setPricingNotes] = useState('');
  const [customNotes, setCustomNotes]   = useState('');
  const [escalationRules, setEscalationRules] = useState([]);
  const [newService, setNewService]     = useState('');

  // Suggest-FAQs state
  const [suggesting, setSuggesting]   = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // FAQ usage analytics (last 30 days, normalised question → count)
  const [faqUsage, setFaqUsage]   = useState({});

  const limits = pack?.limits || DEFAULT_LIMITS;

  // ── Load ────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/knowledge-pack/me`, { headers: auth });
      const d = r.data;
      setPack(d);

      // Empty-state seed: when the user has no overrides yet, prefill the
      // editor with the top 3 default FAQs as suggestions they can tweak.
      const ov = d.overrides || {};
      if ((ov.faqs || []).length === 0) {
        const top = (d.defaults?.faqs || []).slice(0, 3).map((f) => ({
          question: f.question || '', answer: f.answer || '',
        }));
        setFaqs(top);
      } else {
        setFaqs((ov.faqs || []).map((f) => ({
          question: f.question || '', answer: f.answer || '',
        })));
      }
      setServices(ov.services || []);
      setPricingNotes(ov.pricing_notes || '');
      setCustomNotes(ov.custom_notes || '');
      setEscalationRules(ov.escalation_rules || []);
      setFaqUsage((d.faq_usage && d.faq_usage.last_30_days) || {});
    } catch (e) {
      toast.error('Could not load your knowledge pack.');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  // ── FAQ helpers ─────────────────────────────────────────────────────────
  const addFaq = () => {
    if (faqs.length >= limits.max_faqs) {
      toast.error(`You can have up to ${limits.max_faqs} FAQs.`);
      return;
    }
    setFaqs([...faqs, { question: '', answer: '' }]);
  };
  const updateFaq = (i, field, value) =>
    setFaqs(faqs.map((f, idx) => idx === i ? { ...f, [field]: value } : f));
  const removeFaq = (i) => setFaqs(faqs.filter((_, idx) => idx !== i));

  // ── Suggest FAQs ───────────────────────────────────────────────────────
  const fetchSuggestions = async () => {
    if (faqs.length >= limits.max_faqs) {
      toast.error('You already have the maximum number of FAQs.');
      return;
    }
    setSuggesting(true);
    setSuggestions([]);
    try {
      const r = await axios.post(
        `${API}/knowledge-pack/suggest-faqs`,
        {
          industry:       pack?.industry || '',
          business_name:  '',  // backend falls back to agent.brokerage_name
          city:           '',  // backend falls back to agent.display_market
          services,
        },
        { headers: auth },
      );
      const items = (r.data?.suggestions || []).filter((s) => {
        const q = (s.question || '').trim().toLowerCase();
        if (!q) return false;
        // Filter out anything we already have in the editor (server already
        // dedupes against saved overrides + defaults; this catches new
        // editor-only additions)
        return !faqs.some((f) => (f.question || '').trim().toLowerCase() === q);
      });
      setSuggestions(items);
      if (items.length === 0) {
        toast.info("No new suggestions right now — try saving what you have first.");
      } else if (r.data?.source === 'fallback') {
        toast.success('Suggested FAQs loaded from industry defaults.');
      } else {
        toast.success('FAQ suggestions ready.');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.data?.message;
      toast.error(msg || 'Could not generate suggestions.');
    } finally {
      setSuggesting(false);
    }
  };

  const acceptSuggestion = (idx) => {
    const s = suggestions[idx];
    if (!s) return;
    if (faqs.length >= limits.max_faqs) {
      toast.error('You already have the maximum number of FAQs.');
      return;
    }
    // Skip if user already added this one to the editor
    const dup = faqs.some(
      (f) => (f.question || '').trim().toLowerCase() ===
             (s.question || '').trim().toLowerCase(),
    );
    if (!dup) {
      setFaqs([...faqs, { question: s.question, answer: s.answer }]);
    }
    setSuggestions(suggestions.filter((_, i) => i !== idx));
  };

  const dismissSuggestion = (idx) =>
    setSuggestions(suggestions.filter((_, i) => i !== idx));

  // ── Usage badge per FAQ ───────────────────────────────────────────────
  const usageCountFor = (question) => {
    const norm = (question || '').trim().toLowerCase().replace(/\s+/g, ' ');
    return faqUsage[norm] || 0;
  };
  const usageLabel = (count) => {
    if (count <= 0) return 'Not used yet';
    return `Used ${count}× this month`;
  };

  // ── Services helpers ───────────────────────────────────────────────────
  const addService = () => {
    const v = newService.trim();
    if (!v) return;
    if (services.length >= limits.max_services) {
      toast.error(`You can have up to ${limits.max_services} services.`);
      return;
    }
    if (services.some((s) => s.toLowerCase() === v.toLowerCase())) {
      setNewService('');
      return;
    }
    setServices([...services, v]);
    setNewService('');
  };
  const removeService = (i) => setServices(services.filter((_, idx) => idx !== i));

  // ── Escalation rules helpers ───────────────────────────────────────────
  const addRule = () => {
    if (escalationRules.length >= limits.max_escalation_rules) {
      toast.error(`You can have up to ${limits.max_escalation_rules} rules.`);
      return;
    }
    setEscalationRules([...escalationRules, '']);
  };
  const updateRule = (i, value) =>
    setEscalationRules(escalationRules.map((r, idx) => (idx === i ? value : r)));
  const removeRule = (i) =>
    setEscalationRules(escalationRules.filter((_, idx) => idx !== i));

  // ── Save ────────────────────────────────────────────────────────────────
  const save = async () => {
    // Drop empty FAQs / rules before saving (server also sanitises)
    const cleanedFaqs = faqs.filter(
      (f) => (f.question || '').trim() && (f.answer || '').trim(),
    );
    if (faqs.length > 0 && cleanedFaqs.length === 0) {
      toast.error('Each FAQ needs both a question and an answer.');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/knowledge-pack/save`,
        {
          faqs: cleanedFaqs,
          escalation_rules: escalationRules.filter((s) => (s || '').trim()),
          services,
          pricing_notes: pricingNotes,
          custom_notes:  customNotes,
        },
        { headers: auth },
      );
      setPack(r.data);
      toast.success('Your AI has been updated. Voice + SMS will use this immediately.');
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.data?.message;
      toast.error(msg || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex justify-center" data-testid="dashboard-knowledge-loading">
        <Loader2 className="animate-spin text-muted-foreground" size={24} />
      </div>
    );
  }

  const industry        = pack?.industry || '';
  const isTradeIndustry = industry && industry !== 'salon' && industry !== 'real_estate';
  const hasOverrides    = pack?.has_overrides;

  return (
    <div className="p-6 sm:p-10 max-w-4xl space-y-6" data-testid="dashboard-knowledge-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen size={20} /> Your AI&apos;s knowledge
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Customise how your AI answers customers. Changes apply instantly to
            voice calls and SMS replies.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-knowledge-pack-btn"
        >
          {saving
            ? <><Loader2 size={14} className="mr-1 animate-spin"/> Saving…</>
            : <><Save size={14} className="mr-1"/> Save changes</>}
        </Button>
      </header>

      {!isTradeIndustry && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-800">
          <CardContent className="pt-4 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>
              The Industry Knowledge Editor is currently available for trade
              industries (drywall, plumbing, hvac, bathroom remodeling, general
              construction). Your account is set up for{' '}
              <strong>{industry || 'a non-trade industry'}</strong> — saved
              changes will be stored and used as soon as you switch industries.
            </span>
          </CardContent>
        </Card>
      )}

      {!hasOverrides && isTradeIndustry && (
        <Card
          data-testid="empty-state-banner"
          className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/40 dark:border-emerald-800"
        >
          <CardContent className="pt-4 text-sm text-emerald-900 dark:text-emerald-200 flex items-start gap-2">
            <Sparkles size={16} className="mt-0.5 shrink-0" />
            <span>
              Customize how your AI answers customers — start by editing the
              suggested FAQs below or add your most-asked questions.
            </span>
          </CardContent>
        </Card>
      )}

      {/* A. FAQs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Your most common customer questions
          </CardTitle>
          <CardDescription>
            Up to {limits.max_faqs} FAQs. Customer-authored answers are the
            highest-priority knowledge your AI uses.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {faqs.map((f, i) => {
            const count = usageCountFor(f.question);
            return (
              <div
                key={i}
                className="space-y-2 border border-border/60 rounded-md p-3"
                data-testid={`faq-row-${i}`}
              >
                <div className="flex items-start gap-2">
                  <Input
                    placeholder='e.g. "Do you accept HOA work?"'
                    value={f.question}
                    onChange={(e) => updateFaq(i, 'question', e.target.value)}
                    data-testid={`faq-question-${i}`}
                  />
                  <span
                    className={
                      'shrink-0 self-center text-[11px] px-2 py-1 rounded-full whitespace-nowrap '
                      + (count > 0
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                          : 'bg-muted text-muted-foreground')
                    }
                    title="How often your AI used this answer in the last 30 days"
                    data-testid={`faq-usage-badge-${i}`}
                  >
                    {usageLabel(count)}
                  </span>
                </div>
                <Textarea
                  placeholder='Your AI will use this answer (rephrased to sound natural).'
                  value={f.answer}
                  rows={2}
                  onChange={(e) => updateFaq(i, 'answer', e.target.value)}
                  data-testid={`faq-answer-${i}`}
                />
                <div className="flex justify-end">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => removeFaq(i)}
                    data-testid={`faq-remove-${i}`}
                  >
                    <Trash2 size={14} className="mr-1"/> Remove
                  </Button>
                </div>
              </div>
            );
          })}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline" onClick={addFaq}
              disabled={faqs.length >= limits.max_faqs}
              data-testid="faq-add-btn"
            >
              <Plus size={14} className="mr-1"/> Add FAQ
            </Button>
            <Button
              variant="secondary" onClick={fetchSuggestions}
              disabled={suggesting || faqs.length >= limits.max_faqs}
              data-testid="suggest-faqs-btn"
              title={faqs.length >= limits.max_faqs
                ? 'You already have the maximum number of FAQs.'
                : 'Get AI-generated FAQ ideas based on your business'}
            >
              {suggesting
                ? <><Loader2 size={14} className="mr-1 animate-spin"/> Generating suggestions…</>
                : <><Wand2 size={14} className="mr-1"/> Suggest FAQs</>}
            </Button>
            {faqs.length >= limits.max_faqs && (
              <span
                className="self-center text-xs text-muted-foreground"
                data-testid="faq-max-reached-hint"
              >
                You already have the maximum number of FAQs.
              </span>
            )}
          </div>

          {suggestions.length > 0 && (
            <div className="space-y-2 pt-2" data-testid="faq-suggestions">
              <p className="text-xs text-muted-foreground">
                Tap <strong>Accept</strong> to add a suggestion to your FAQs (you&apos;ll
                still need to click Save).
              </p>
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  data-testid={`faq-suggestion-${i}`}
                  className="rounded-md border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-800 p-3 space-y-2"
                >
                  <div className="text-sm font-medium">{s.question}</div>
                  <div className="text-sm text-muted-foreground">{s.answer}</div>
                  <div className="flex gap-2">
                    <Button
                      size="sm" variant="default"
                      onClick={() => acceptSuggestion(i)}
                      data-testid={`faq-suggestion-accept-${i}`}
                    >
                      <Check size={14} className="mr-1"/> Accept
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => dismissSuggestion(i)}
                      data-testid={`faq-suggestion-dismiss-${i}`}
                    >
                      <X size={14} className="mr-1"/> Dismiss
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {faqs.length === 0 && (pack?.defaults?.faqs?.length || 0) > 0 && (
            <details className="text-xs text-muted-foreground pt-2">
              <summary className="cursor-pointer">
                See the default FAQs your AI is using right now
              </summary>
              <ul className="mt-2 space-y-1 pl-4 list-disc">
                {pack.defaults.faqs.slice(0, 5).map((f, i) => (
                  <li key={i}><strong>{f.question}</strong> — {f.answer}</li>
                ))}
              </ul>
            </details>
          )}
        </CardContent>
      </Card>

      {/* B. Services Offered */}
      <Card>
        <CardHeader>
          <CardTitle>Services you offer</CardTitle>
          <CardDescription>
            Helps your AI describe what you do and qualify leads.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="services-list">
            {services.map((s, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-sm"
                data-testid={`service-tag-${i}`}
              >
                {s}
                <button
                  type="button" onClick={() => removeService(i)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={`Remove ${s}`}
                  data-testid={`service-remove-${i}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder='e.g. "Drywall repair"'
              value={newService}
              onChange={(e) => setNewService(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addService(); } }}
              data-testid="service-input"
            />
            <Button
              variant="outline" onClick={addService}
              disabled={!newService.trim() || services.length >= limits.max_services}
              data-testid="service-add-btn"
            >
              <Plus size={14} className="mr-1"/> Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* C. Pricing Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Pricing notes</CardTitle>
          <CardDescription>
            Optional: pricing guidance for your AI (e.g. &quot;Free estimates&quot;,
            &quot;Starting at $150&quot;). Replaces the default industry pricing policy.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Label htmlFor="pricing-notes" className="sr-only">Pricing notes</Label>
          <Textarea
            id="pricing-notes"
            rows={3}
            value={pricingNotes}
            onChange={(e) => setPricingNotes(e.target.value.slice(0, limits.max_pricing_notes))}
            placeholder='e.g. "Free in-home estimates. Most repairs $200–$500. We do not quote without seeing the job."'
            data-testid="pricing-notes-input"
          />
          <p className="text-xs text-muted-foreground mt-1 text-right">
            {pricingNotes.length} / {limits.max_pricing_notes}
          </p>
        </CardContent>
      </Card>

      {/* D. Escalation Rules */}
      <Card>
        <CardHeader>
          <CardTitle>Escalation rules</CardTitle>
          <CardDescription>
            When should your AI hand off to a human?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {escalationRules.map((rule, i) => (
            <div key={i} className="flex gap-2" data-testid={`rule-row-${i}`}>
              <Input
                placeholder='e.g. "If job > $5k, escalate to owner"'
                value={rule}
                onChange={(e) => updateRule(i, e.target.value)}
                data-testid={`rule-input-${i}`}
              />
              <Button
                variant="ghost" size="icon"
                onClick={() => removeRule(i)}
                aria-label="Remove rule"
                data-testid={`rule-remove-${i}`}
              >
                <Trash2 size={14}/>
              </Button>
            </div>
          ))}
          <Button
            variant="outline" onClick={addRule}
            disabled={escalationRules.length >= limits.max_escalation_rules}
            data-testid="rule-add-btn"
          >
            <Plus size={14} className="mr-1"/> Add rule
          </Button>
        </CardContent>
      </Card>

      {/* E. Custom Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Anything else your AI should always know</CardTitle>
          <CardDescription>
            Free-form business-specific notes. The AI will treat these as
            authoritative.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={5}
            value={customNotes}
            onChange={(e) => setCustomNotes(e.target.value.slice(0, limits.max_custom_notes))}
            placeholder='e.g. "We are residential only — never commercial. We have a 5-year warranty on all labor. We service Santa Cruz County only."'
            data-testid="custom-notes-input"
          />
          <p className="text-xs text-muted-foreground mt-1 text-right">
            {customNotes.length} / {limits.max_custom_notes}
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end pt-4">
        <Button
          onClick={save}
          disabled={saving}
          size="lg"
          data-testid="save-knowledge-pack-btn-bottom"
        >
          {saving
            ? <><Loader2 size={14} className="mr-1 animate-spin"/> Saving…</>
            : <><Save size={14} className="mr-1"/> Save changes</>}
        </Button>
      </div>
    </div>
  );
}
